// ============================================================
// Per-model explainability + feature importance (illustrative).
// Drives the generic ModelExplainModal so every ML surface gets a
// clickable, chart-form Explainability view and a separate
// Feature-importance view — mirroring the Net Income Forecast tab.
//
// Values are synthetic but plausible; in production these come from
// SHAP on the trained models and the per-prediction decomposition.
// ============================================================

export type ExplainMode = "explain" | "importance";

export interface FeatureImp {
  feature: string;
  value: number; // signed mean-SHAP contribution (− pushes target down, + up)
}
export interface DriverItem {
  label: string;
  value: number; // signed contribution to one example prediction (in target units)
}
export interface Metric {
  label: string;
  value: string;
}

export interface ModelExplainSpec {
  key: string;
  name: string;          // model surface name
  method: string;        // model family (explainable)
  unit: "money" | "raw"; // driver units
  posLabel: string;      // right-axis annotation on the SHAP chart
  negLabel: string;      // left-axis annotation
  predictionLabel: string; // header for the example-prediction decomposition
  importance: FeatureImp[];
  drivers: DriverItem[];
  metrics: Metric[];
  narrativeImportance: string;
  narrativeExplain: string;
}

export const MODEL_EXPLAIN: Record<string, ModelExplainSpec> = {
  // ---- Layer 1 — Portfolio Billing Intelligence ----
  l1: {
    key: "l1",
    name: "Layer 1 — Portfolio Billing Intelligence",
    method: "Gradient-boosted trees (LightGBM / XGBoost) · hierarchical Bayesian aggregation · SHAP",
    unit: "money",
    posLabel: "raises over-billing →",
    negLabel: "← raises under-billing",
    predictionLabel: "Example project — predicted quarter-end over/under position",
    importance: [
      { feature: "Percent complete", value: 0.28 },
      { feature: "Project type (clean room)", value: -0.22 },
      { feature: "Cost-to-date ratio", value: 0.19 },
      { feature: "Change-order status", value: -0.16 },
      { feature: "Billed-to-date", value: 0.14 },
      { feature: "Customer tier", value: 0.11 },
      { feature: "Retainage policy", value: -0.09 },
      { feature: "Contract type (GMP)", value: 0.07 },
    ],
    drivers: [
      { label: "Stage / percent complete", value: 1_350_000 },
      { label: "Cost-to-date vs budget", value: 640_000 },
      { label: "Approved change orders", value: -520_000 },
      { label: "Clean-room mix", value: -410_000 },
      { label: "Retainage held", value: -180_000 },
    ],
    metrics: [
      { label: "MAPE", value: "7.8%" },
      { label: "MASE", value: "0.41" },
      { label: "80% PI cov", value: "82.4%" },
      { label: "Clean-room replay", value: "PASS" },
    ],
    narrativeImportance:
      "Stage and cost-to-date dominate the billing position; clean-room mix and approved change orders pull it the other way. Importances are mean |SHAP| across the live WIP portfolio — the same attributions that explain any single project.",
    narrativeExplain:
      "Each project's predicted over/under position decomposes into the features above; positions aggregate to the portfolio via a hierarchical Bayesian step that produces the calibrated interval. Mix shifts are reflected automatically because the project mix is the input.",
  },

  // ---- Layer 2 — AR Anomaly + Customer Risk ----
  l2: {
    key: "l2",
    name: "Layer 2 — AR Anomaly + Customer Risk",
    method: "Gradient-boosted trees + SHAP · CUSUM / Page-Hinkley change-point detection",
    unit: "raw",
    posLabel: "increases risk →",
    negLabel: "← decreases risk",
    predictionLabel: "Example customer — composite risk score (K-028) build-up",
    importance: [
      { feature: "Pay-cycle deviation vs baseline", value: 0.31 },
      { feature: "Paydex score", value: -0.24 },
      { feature: "Days since last payment", value: 0.18 },
      { feature: "Tier (sub-to-sub)", value: 0.15 },
      { feature: "Pay-if-paid clause", value: 0.12 },
      { feature: "Exposure-weighted AR", value: 0.09 },
      { feature: "Dispute frequency", value: 0.08 },
      { feature: "Retainage-release lag", value: 0.06 },
    ],
    drivers: [
      { label: "Pay-cycle drift (+11d vs baseline)", value: 22 },
      { label: "Tier — sub-to-sub", value: 14 },
      { label: "Pay-if-paid exposure", value: 11 },
      { label: "Strong Paydex history", value: -16 },
      { label: "Recent dispute", value: 8 },
    ],
    metrics: [
      { label: "Precision", value: "0.92" },
      { label: "Recall", value: "0.86" },
      { label: "AUC-ROC", value: "0.94" },
      { label: "FP rate", value: "≤5%" },
    ],
    narrativeImportance:
      "Behavioural pay-cycle drift is the strongest single signal; a healthy Paydex history is the strongest mitigant. Importances are mean |SHAP| across the customer book — every alert carries the same per-customer attribution.",
    narrativeExplain:
      "The composite K-028 score builds additively from each feature's contribution, so an analyst can read exactly why a customer is flagged. A CUSUM / Page-Hinkley change-point detector fires the early-warning 30–45 days before the shift reaches the aging report.",
  },

  // ---- Layer 3 — Net Income Forecast Engine ----
  l3: {
    key: "l3",
    name: "Layer 3 — Net Income Forecast Engine",
    method: "Holt-Winters (damped) · gradient-boosted benchmark · SHAP on 237 leak-free features",
    unit: "money",
    posLabel: "raises net income →",
    negLabel: "← lowers net income",
    predictionLabel: "Next quarter — forecast build-up (level / trend / seasonal)",
    importance: [
      { feature: "Net income — lag 1Q", value: 0.34 },
      { feature: "Revenue — lag 1Q", value: 0.21 },
      { feature: "Rolling 4Q NI mean", value: 0.18 },
      { feature: "Seasonal factor (quarter)", value: 0.15 },
      { feature: "Backlog conversion", value: 0.12 },
      { feature: "Gross-margin trend", value: 0.10 },
      { feature: "Nonres construction (FRED)", value: 0.07 },
      { feature: "10Y Treasury (FRED)", value: -0.05 },
    ],
    drivers: [
      { label: "Level (recent results)", value: 1_620_000 },
      { label: "Trend (backlog ramp, damped)", value: 720_000 },
      { label: "Q3 seasonal uplift", value: 410_000 },
      { label: "Margin normalization", value: -380_000 },
      { label: "Rate headwind", value: -160_000 },
    ],
    metrics: [
      { label: "qMAPE", value: "23.4%" },
      { label: "Annual MAPE", value: "17.4%" },
      { label: "80% PI cov", value: "80%" },
      { label: "Target", value: "±5% annual" },
    ],
    narrativeImportance:
      "Recent net income and revenue lags carry the most weight, with seasonality and backlog conversion next; macro covariates contribute at the margin. Importances are mean |SHAP| from the gradient-boosted benchmark over the leak-free feature set.",
    narrativeExplain:
      "The deployed forecast is a damped Holt-Winters model: each quarter is assembled from a level, a trend and a seasonal component, shown above in dollars. Intervals come from a 1,000-path simulation; the gradient-boosted benchmark provides the SHAP attribution.",
  },

  // ---- Net Cash Position — trend & forecast ----
  netcash: {
    key: "netcash",
    name: "Net Cash Position — Trend & Forecast",
    method: "Working-capital cash bridge · Holt-Winters trend · SHAP · reconciles to GL",
    unit: "money",
    posLabel: "raises net cash →",
    negLabel: "← drains net cash",
    predictionLabel: "Next quarter — net-cash bridge (opening → closing)",
    importance: [
      { feature: "AR collections pace (DSO)", value: 0.30 },
      { feature: "AP / disbursement timing", value: -0.22 },
      { feature: "Project billings (over-billing)", value: 0.20 },
      { feature: "Retainage release", value: 0.14 },
      { feature: "Backlog conversion", value: 0.12 },
      { feature: "Seasonal cash cycle", value: 0.08 },
      { feature: "Short-term debt service", value: -0.10 },
      { feature: "Rate environment (macro)", value: -0.05 },
    ],
    drivers: [
      { label: "AR collections", value: 6_200_000 },
      { label: "Project billings", value: 3_400_000 },
      { label: "AP disbursements", value: -5_100_000 },
      { label: "Debt service", value: -1_300_000 },
      { label: "Retainage release", value: 900_000 },
    ],
    metrics: [
      { label: "MAPE", value: "6.1%" },
      { label: "80% PI cov", value: "81%" },
      { label: "Horizon", value: "6 mo" },
      { label: "GL reconcile", value: "PASS" },
    ],
    narrativeImportance:
      "Net cash is driven first by how fast AR converts and when AP goes out; billings and retainage release add to it, debt service and the rate environment pull it down. Importances are mean |SHAP| over the cash bridge.",
    narrativeExplain:
      "The forecast is a working-capital bridge: opening cash plus expected collections and billings, less disbursements and debt service, plus retainage release — every line reconciles to the general ledger, and the Holt-Winters trend sets the run-rate.",
  },

  // ---- Simulator — Customer Risk Scoring ----
  sim: {
    key: "sim",
    name: "Simulator — Customer Risk Scoring",
    method: "Additive weighted scoring · 11 features · transparent SHAP-style attribution",
    unit: "raw",
    posLabel: "increases risk →",
    negLabel: "← decreases risk",
    predictionLabel: "Example customer — composite score build-up (points)",
    importance: [
      { feature: "Payment terms (net days)", value: 0.26 },
      { feature: "Paydex score", value: -0.24 },
      { feature: "Pay-if-paid clause", value: 0.18 },
      { feature: "Tier (sub-to-sub)", value: 0.16 },
      { feature: "Contract type", value: 0.10 },
      { feature: "Retainage %", value: 0.09 },
      { feature: "Contract value (exposure)", value: 0.08 },
      { feature: "Geography / NAICS", value: 0.05 },
    ],
    drivers: [
      { label: "Net-60 payment terms", value: 18 },
      { label: "Pay-if-paid clause", value: 12 },
      { label: "Sub-to-sub tier", value: 10 },
      { label: "Large contract exposure", value: 7 },
      { label: "Strong Paydex history", value: -16 },
    ],
    metrics: [
      { label: "AUC-ROC", value: "0.94" },
      { label: "Brier", value: "0.082" },
      { label: "Log-loss", value: "0.31" },
      { label: "Threshold", value: "75" },
    ],
    narrativeImportance:
      "Slow payment terms and a pay-if-paid clause push risk up the most; a strong Paydex history is the biggest mitigant. Because the model is additive with explicit weights, the global importance is exactly the sum of per-feature weights — no hidden interactions.",
    narrativeExplain:
      "Every score is the sum of explicit feature weights, so an underwriter can read precisely why a customer scores where it does. The live driver panel shows this for the current inputs; the build-up above is a representative example.",
  },
};
